import { Text } from '@volkswagen-onehub/components-core';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { MultipleCTASelection } from 'src/components';
import { isMyDealerServiceAvailable, updateMapFromConcessions, updateMapVariables, useOneFormContext } from 'src/feature-app';
import { CTAsHorario } from 'src/feature-app/InputsValues';
import LoadScriptInstance from 'src/feature-app/NewMap/Loadscript';
import { LayoutTemplate, RenderController } from 'src/feature-app/Screen';
import { LocateUser } from 'src/forms/CitaPosventa/NuevaCitaPosventa';
import { getMapaTitle } from 'src/forms/Leads/helpers/getMapaTitle';
import { TIME_FOR_TRANSFORM } from 'src/globals';
import { OneFormState, Steps } from 'src/types';
import { JourneyLeadsWrapper } from './JourneyLeadsWrapper';

enum MapaLeadsSteps {
	Dealer,
	Horario,
	SeleccionCita,
	SeleccionFecha,
}

interface MapaLeadsProps {
	showHorario?: boolean;
	formName?: string;
	title?: JSX.Element;
}

const isLastScreen = (steps: Steps[], multiStepScreenIndex: number) => steps.length - 1 === multiStepScreenIndex;
const isFirstScreen = (multiStepScreenIndex: number) => multiStepScreenIndex === 0;

export function MapaLeads(props: MapaLeadsProps) {
	const { formName, showHorario, title } = props;
	const dispatch = useDispatch();
	const store = useStore();
	const { formInfo, dealersInfo } = useSelector((state: OneFormState) => state);
	const { firstMapLoad, multiStepScreenIndex, navigationMovingForward } = formInfo;
	const { selectedInstallation } = dealersInfo;

	const {
		handleScreenChange,
		// setWaitForNextStep,
		setNextMultiStep,
		setNextMultiStepScreenIndex,
		setPreviousMultiStep,
		setWaitForPreviousStep,
		waitForPreviousStep,
		// waitForNextStep,
		setIsFullScreen,
		setShowPreviousStep,
		previousStep,
	} = useOneFormContext();

	const getTitle = (short?: boolean) => {
		if (title) {
			return title;
		} else {
			return getMapaTitle(formName, short);
		}
	};

	const getShortTitle = () => {
		return getTitle(true);
	};

	let stepsHorario: Steps[] = [
		{
			title: getTitle(),
			alternateTitle: getShortTitle(), // título más corto para el previus step
			fields: isMyDealerServiceAvailable() ? null : <LocateUser />,
			screenIndex: MapaLeadsSteps.Dealer,
			name: 'MapSearch',
			outputs: ['location'],
			outputsText: ['location'],
			showOverflow: true,
		},
		{
			screenIndex: MapaLeadsSteps.Horario,
			name: 'MapSlotPref',
			title: (
				<>
					<Text bold>¿Cúando</Text> prefieres pasar por el{' '}
					<span style={{ whiteSpace: 'nowrap' }}>
						punto de venta?<sup style={{ top: 'auto', fontSize: '100%' }}>*</sup>
					</span>
				</>
			),
			fields: <LayoutTemplate element={<MultipleCTASelection ctaValues={CTAsHorario} name="horario" nextOnClick />} />,
			outputs: ['horario'],
			outputsText: ['horario'],
		},
		{
			screenIndex: MapaLeadsSteps.SeleccionCita,
			name: 'MapSelect',
			title: <></>,
			alternateTitle: getShortTitle(), // título más corto para el previus step
			fields: <JourneyLeadsWrapper />,
			outputs: ['installation'],
			outputsText: ['installation'],
			fullScreen: true,
			multiStepLastScreen: true,
			hidePreviousStep: true,
		},
	];

	let stepsWithoutHorario: Steps[] = [
		{
			title: getTitle(),
			alternateTitle: getShortTitle(), // título más corto para el previus step
			fields: <LocateUser />,
			screenIndex: MapaLeadsSteps.Dealer,
			name: 'MapSearch',
			outputs: ['location'],
			outputsText: ['location'],
			showOverflow: true,
		},
		{
			screenIndex: MapaLeadsSteps.Dealer + 1,
			name: 'MapSelect',
			title: <></>,
			alternateTitle: getShortTitle(), // título más corto para el previus step
			fields: <JourneyLeadsWrapper />,
			outputs: ['installation'],
			outputsText: ['installation'],
			fullScreen: true,
			multiStepLastScreen: true,
			hidePreviousStep: true,
		},
	];

	const steps = showHorario ? stepsHorario : stepsWithoutHorario;
	const [selectedStep, setSelectedStep] = useState<Steps>(null);

	// En algunas ocasiones no funciona correctamente el setShowPreviousStep(false) por lo que conviene aplicar un timeout.
	const setPreviousStepWithTimeout = (boolean: boolean, ms?: number) => {
		setShowPreviousStep(boolean);
		setTimeout(
			() => {
				setShowPreviousStep(boolean);
			},
			ms ? ms : TIME_FOR_TRANSFORM,
		);
	};

	// Hay que esperar a que se cree el mapa de google para tener acceso a window.google
	const onMapLoad = () => {
		handleFirstLoad();
	};

	const handleFirstLoad = async () => {
		if (firstMapLoad) {
			// Inicialización
			initializeMultiStep();
		} else {
			if (navigationMovingForward || navigationMovingForward === null || navigationMovingForward === undefined) {
				// Inicialización cuando se va hacia adelante una vez inicializado el mapa o después de cerrar el layer y abrir en el mapa.
				initializeMultiStep();
			} else {
				// En caso de que estemos en la última pantalla, desactivamos el wait for next step.
				const newWaitForNextStep = !isLastScreen(steps, multiStepScreenIndex);
				// Lo mismo para previous, solo le desactivamos si estamos en la primera.
				const newWaitForPreviousStep = !isFirstScreen(multiStepScreenIndex);

				updateWaitForNextAndPreviousStep(newWaitForNextStep, newWaitForPreviousStep);
				updateScreenChangeVariables(multiStepScreenIndex);
				setPreviousStepWithTimeout(false);
			}
		}
		setNextMultiStep(() => nextMapaLeads);
		setPreviousMultiStep(() => previousMapaLeads);
	};

	const initializeMultiStep = async () => {
		if (isMyDealerServiceAvailable()) {
			await updateMapFromConcessions(dispatch, dealersInfo.installations);
			updateWaitForNextAndPreviousStep(false, true);
			updateScreenChangeVariables(steps.length - 1);
			setPreviousStepWithTimeout(false);
		} else if (selectedInstallation) {
			await updateMapVariables(dispatch, selectedInstallation);
			updateWaitForNextAndPreviousStep(false, true);
			updateScreenChangeVariables(steps.length - 1);
			setPreviousStepWithTimeout(false);
		} else {
			updateWaitForNextAndPreviousStep(true, false);
			updateScreenChangeVariables(0);
		}
	};

	const updateWaitForNextAndPreviousStep = (waitForNextStep: boolean, waitForPreviousStep: boolean) => {
		if (waitForNextStep !== null) {
			dispatch({ type: 'UPDATE_WAIT_FOR_NEXT_STEP', payload: waitForNextStep });
		}
		if (waitForPreviousStep !== null) {
			setWaitForPreviousStep(waitForPreviousStep);
		}
	};

	const nextMapaLeads = () => {
		const { multiStepScreenIndex } = store.getState().formInfo;

		handleScreenChange();

		// Una vez avanzamos dentro de los multisteps se activa el wait previous step para volver hacia atrás dentro del propio multistep.
		if (!waitForPreviousStep || multiStepScreenIndex === 0) {
			updateWaitForNextAndPreviousStep(null, true);
		}

		if (steps[multiStepScreenIndex + 1]?.multiStepLastScreen) {
			setPreviousStepWithTimeout(multiStepScreenIndex != 0);
		}

		setTimeout(() => {
			updateScreenChangeVariables(multiStepScreenIndex + 1);
			// Si estamos en el último paso del multistep se desactiva el wait next step para poder salir del multistep y avanzar hacia la siguiente pantalla.
			if (steps[multiStepScreenIndex + 1]?.multiStepLastScreen) {
				updateWaitForNextAndPreviousStep(false, null);
			}
		}, TIME_FOR_TRANSFORM);
	};

	const previousMapaLeads = () => {
		if (isMyDealerServiceAvailable()) {
			previousStep();
			return;
		}
		const { multiStepScreenIndex, waitForNextStep } = store.getState().formInfo;

		handleScreenChange();

		// Se desactiva el wait previous step en la segunda pantalla para que en el caso de volver hacia atrás en la primera salte de step y no busque dentro de los multisteps.
		if (multiStepScreenIndex === 1) {
			updateWaitForNextAndPreviousStep(null, false);
		}

		if (!waitForNextStep) {
			updateWaitForNextAndPreviousStep(true, null);
		}

		// Se fija en la pantalla previa a la primera para que cuando se cambie a la primera pantalla el CTA de anterior pase del multistep al step anterior.
		setTimeout(() => {
			updateScreenChangeVariables(multiStepScreenIndex - 1);
		}, TIME_FOR_TRANSFORM);
	};

	const updateScreenChangeVariables = (newMultiStepScreenIndex: number) => {
		if (steps[newMultiStepScreenIndex]) {
			setNextMultiStepScreenIndex(newMultiStepScreenIndex);
			dispatch({ type: 'UPDATE_MULTISTEP_SCREENINDEX', payload: newMultiStepScreenIndex });
			setSelectedStep(steps[newMultiStepScreenIndex]);
			setIsFullScreen(steps[newMultiStepScreenIndex].fullScreen);
			setPreviousStepWithTimeout(!steps[newMultiStepScreenIndex].hidePreviousStep);
		}
	};

	useEffect(() => {
		dispatch({ type: 'UPDATE_MULTISTEPS', payload: steps });
		handleFirstLoad();

		return () => {
			dispatch({ type: 'UPDATE_FIRST_MAP_LOAD', payload: false });
			setPreviousStepWithTimeout(true);
			updateWaitForNextAndPreviousStep(false, false);
		};
	}, []);

	return selectedStep ? (
		<LoadScriptInstance onMapLoad={onMapLoad}>
			<RenderController screenType="layer-screen" title={selectedStep.title} fields={selectedStep.fields} key={selectedStep.screenIndex} />
		</LoadScriptInstance>
	) : null;
}
